/* hulkWishlistPLPButtons.js — single source for Wishlist (PLP + Cart + QuickBuy augmentation) */

(() => {
  /* ---------------- Core (helpers, API, state) ---------------- */

  if (!window.WishlistCore) {
    const DEFAULT_WISHLIST_NAME = "My Wishlist";

    function createHeaders(includeEncryptToken = true) {
      const headers = {
        Authorization: window.hulkappsWishlist?.bearerToken || "",
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      if (includeEncryptToken && window.hulkappsWishlist?.encryptToken) {
        headers["Encrypt-token"] = window.hulkappsWishlist.encryptToken;
      }
      return headers;
    }

    function createUrlWithParams(baseUrl, params) {
      const url = new URL(baseUrl);
      Object.keys(params || {}).forEach((k) => {
        const v = params[k];
        if (v !== undefined && v !== null) url.searchParams.append(k, v);
      });
      return url;
    }

    async function makeRequest(url, headers, options = {}) {
      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }

    async function getDefaultWishlistId() {
      const cid = window.hulkappsWishlist?.customerId;
      if (!cid) return null; // guests don't have lists
      if (window.hulkappsWishlist.defaultWishlistId)
        return window.hulkappsWishlist.defaultWishlistId;

      const listsUrl = createUrlWithParams(
        "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/lists",
        { customer_id: cid }
      );
      const listsResp = await makeRequest(listsUrl, createHeaders());
      const lists = Array.isArray(listsResp)
        ? listsResp
        : listsResp?.data || [];
      let def =
        lists.find((l) => l.is_default) ||
        lists.find((l) => (l.name || "").trim() === DEFAULT_WISHLIST_NAME);

      if (!def) {
        const r = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/create",
          {
            method: "POST",
            headers: createHeaders(),
            body: JSON.stringify({
              name: DEFAULT_WISHLIST_NAME,
              customer_id: String(cid),
            }),
          }
        );
        if (!r.ok) throw new Error(`Create wishlist failed: ${r.status}`);
        const j = await r.json();
        def = { id: j?.data?.id };
      }

      window.hulkappsWishlist.defaultWishlistId = def.id;
      return def.id;
    }

    async function addProductToDefaultWishlist({ productId, variantId }) {
      const isLoggedIn = !!window.hulkappsWishlist?.customerId;

      if (isLoggedIn) {
        const listId = await getDefaultWishlistId();
        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/add_product",
          {
            method: "POST",
            headers: createHeaders(),
            body: JSON.stringify({
              product_id: String(productId),
              customer_id: String(window.hulkappsWishlist.customerId),
              wishlist_ids: [Number(listId)], // array (not string)
              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Add (customer) failed:", res.status, errText);
          if (res.status === 422 && /exist|already/i.test(errText))
            return { ok: true, already: true };
          throw new Error(`Add (customer) failed: ${res.status}`);
        }
        const result = await res.json();
        // Add the variant ID that was actually used to the response
        if (result?.data && variantId) {
          result.data.variant_id = variantId;
        }
        return result;
      } else {
        const gid = window.hulkappsWishlist?.guestId || getSavedGuestUuid();

        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/add_product/guest",
          {
            method: "POST",
            headers: createHeaders(false),
            body: JSON.stringify({
              product_id: String(productId),
              ...(gid ? { uuid: gid } : {}),
              ...(variantId ? { variant_id: String(variantId) } : {}), // send only if we have a server uuid              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Add (guest) failed:", res.status, errText);
          throw new Error(`Add (guest) failed: ${res.status}`);
        }
        // 204 = already existed; no body
        if (res.status === 204) return { ok: true, already: true };
        let data = {};
        try {
          data = await res.json();
        } catch (_) {}

        // Try to capture server-issued UUID from known spots
        const serverUuid =
          data?.data?.uuid ||
          data?.data?.guest_uuid ||
          data?.uuid ||
          data?.guest_uuid ||
          null;
        if (serverUuid && !window.hulkappsWishlist?.guestId) {
          saveGuestUuid(serverUuid);
        }
        // Add the variant ID that was actually used to the response
        if (data?.data && variantId) {
          data.data.variant_id = variantId;
        }
        return data;
      }
    }

    async function findWishlistContainingProduct({ productId, variantId }) {
      const cid = window.hulkappsWishlist?.customerId;
      if (!cid) return null;

      try {
        // Get all wishlists for the customer
        const listsUrl = createUrlWithParams(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/lists",
          { customer_id: cid }
        );
        const listsResp = await makeRequest(listsUrl, createHeaders());
        const lists = Array.isArray(listsResp) ? listsResp : listsResp?.data || [];

        // Search through each wishlist to find the product
        for (const list of lists) {
          const productsUrl = createUrlWithParams(
            "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products",
            {
              wishlist_id: list.id,
              customer_id: cid,
              status: "published",
            }
          );
          const productsResp = await makeRequest(productsUrl, createHeaders());
          const products = Array.isArray(productsResp?.data) ? productsResp.data : [];

          // Check if this wishlist contains the product with matching variant
          const foundProduct = products.find(product => {
            const matchesProduct = Number(product.product_id) === Number(productId);
            const matchesVariant = Number(variantId) 
              ? Number(product.variant_id) === Number(variantId)
              : !product.variant_id; // If no variant specified, match products without variant
            return matchesProduct && matchesVariant;
          });

          if (foundProduct) {
            return list.id;
          }
        }
        return null;
      } catch (err) {
        console.error("Error finding wishlist containing product:", err);
        return null;
      }
    }

    async function removeProductFromWishlist({ productId, variantId }) {
      const isLoggedIn = !!window.hulkappsWishlist?.customerId;

      if (isLoggedIn) {
        // Find which wishlist contains this product
        const listId = await findWishlistContainingProduct({ productId, variantId });
        
        if (!listId) {
          console.warn("Product not found in any wishlist:", productId, variantId);
          return { ok: true, notFound: true };
        }

        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/delete_product",
          {
            method: "POST",
            headers: createHeaders(),
            body: JSON.stringify({
              product_id: String(productId),
              customer_id: String(window.hulkappsWishlist.customerId),
              wishlist_ids: [Number(listId)],
              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Remove (customer) failed:", res.status, errText);
          throw new Error(`Remove (customer) failed: ${res.status}`);
        }
        return res.json();
      } else {
        // For guest users, remove from guest wishlist
        const gid = window.hulkappsWishlist?.guestId || getSavedGuestUuid();

        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/delete_product/guest",
          {
            method: "POST",
            headers: createHeaders(false),
            body: JSON.stringify({
              product_id: String(productId),
              uuid: gid,
              ...(variantId ? { variant_id: String(variantId) } : {}),
            }),
          }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => "");
          console.error("Remove (guest) failed:", res.status, errText);
          throw new Error(`Remove (guest) failed: ${res.status}`);
        }
        return res.json();
      }
    }

    function markButtonAsAdded(anchorEl, actualVariantId = null) {
      if (!anchorEl) return;
      anchorEl.setAttribute("data-added", "true");

      // For PLP buttons, keep href as "#" since we handle clicks directly
      // For cart buttons, set href to wishlist page
      const isPLPButton =
        anchorEl.getAttribute("data-wishlist-trigger") === "plp";
      if (!isPLPButton) {
        anchorEl.href = `${window.shopUrl}/${window.hulkappsWishlist.generalSettings.app_proxy_prefix}/${window.hulkappsWishlist.generalSettings.app_proxy_path}`;
      } else {
        anchorEl.href = "#";
      }

      const pid = Number(anchorEl.getAttribute("data-proid"));
      let variantId = anchorEl.getAttribute("data-varid") ? Number(anchorEl.getAttribute("data-varid")) : null;
      
      // If we have an actual variant ID from the wishlist operation, use that
      if (actualVariantId) {
        variantId = actualVariantId;
        anchorEl.setAttribute("data-varid", actualVariantId);
      }
      
      if (!Number.isNaN(pid)) {
        const arr = (window.hulkappsWishlist.wishlistProductIds =
          window.hulkappsWishlist.wishlistProductIds || []);
        if (!arr.includes(pid)) arr.push(pid);
        
        // Also add to wishlistItems array
        const itemsArr = (window.hulkappsWishlist.wishlistItems =
          window.hulkappsWishlist.wishlistItems || []);
        const itemExists = itemsArr.some(
          item => item.productId === pid && item.variantId === variantId
        );
        if (!itemExists) {
          itemsArr.push({ productId: pid, variantId: variantId });
        }
      }
    }

    function markButtonAsRemoved(anchorEl) {
      if (!anchorEl) return;
      anchorEl.setAttribute("data-added", "false");
      anchorEl.href = "#";
      const pid = Number(anchorEl.getAttribute("data-proid"));
      const variantId = anchorEl.getAttribute("data-varid") ? Number(anchorEl.getAttribute("data-varid")) : null;
      
      if (!Number.isNaN(pid)) {
        const arr = window.hulkappsWishlist.wishlistProductIds || [];
        const index = arr.indexOf(pid);
        if (index > -1) arr.splice(index, 1);
        
        // Also remove from wishlistItems array
        const itemsArr = window.hulkappsWishlist.wishlistItems || [];
        const itemIndex = itemsArr.findIndex(
          item => item.productId === pid && item.variantId === variantId
        );
        if (itemIndex > -1) itemsArr.splice(itemIndex, 1);
      }
    }

    async function fetchWishlistProducts(wishlistId) {
      const url = createUrlWithParams(
        "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products",
        {
          wishlist_id: wishlistId,
          customer_id: window.hulkappsWishlist.customerId,
          status: "published",
          sort_by_date: "desc",
        }
      );
      const resp = await makeRequest(url, createHeaders());
      (resp?.data || []).forEach((row) => {
        const pid = Number(row.product_id);
        const variantId = row.variant_id ? Number(row.variant_id) : null;
        
        // Store in product IDs array for backward compatibility
        if (!window.hulkappsWishlist.wishlistProductIds.includes(pid)) {
          window.hulkappsWishlist.wishlistProductIds.push(pid);
        }
        
        // Store full item data with variant ID
        const itemExists = window.hulkappsWishlist.wishlistItems.some(
          item => item.productId === pid && item.variantId === variantId
        );
        if (!itemExists) {
          window.hulkappsWishlist.wishlistItems.push({
            productId: pid,
            variantId: variantId
          });
        }
      });
    }

    function updateHeaderWishlistCount() {
      const totalCountElement = document.querySelector(
        "[data-totalwishlistitems]"
      );
      
      if (totalCountElement && window.hulkappsWishlist?.wishlistProductIds) {
        if (window.hulkappsWishlist.wishlistProductIds.length > 0) {
        totalCountElement.textContent =
          window.hulkappsWishlist.wishlistProductIds.length;
          totalCountElement.style.removeProperty("display");
          totalCountElement.style.display = "grid !important";
        }
        else {
          totalCountElement.textContent = "";
          totalCountElement.style.display = "none !important";
        }
      }
    }

    async function fetchWishlistData() {
      window.hulkappsWishlist.wishlistProductIds = [];
      window.hulkappsWishlist.wishlistItems = []; // Store full items with variant IDs
      window.hulkappsWishlist._wishlistLoaded = false;
      try {
        if (window.hulkappsWishlist.customerId) {
          const url = createUrlWithParams(
            "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/lists",
            { customer_id: window.hulkappsWishlist.customerId }
          );
          const listsResp = await makeRequest(url, createHeaders());
          const lists = Array.isArray(listsResp)
            ? listsResp
            : listsResp?.data || [];
          await Promise.all(lists.map((l) => fetchWishlistProducts(l.id)));
        } else {
          const cached = getSavedGuestUuid();
          if (cached) {
            saveGuestUuid(cached);
            const url = createUrlWithParams(
              "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products/guest",
              { uuid: cached }
            );

            const resp = await makeRequest(url, createHeaders(false));
            (resp?.data || []).forEach((row) => {
              const pid = Number(row.product_id);
              const variantId = row.variant_id ? Number(row.variant_id) : null;
              
              // Store in product IDs array for backward compatibility
              if (!window.hulkappsWishlist.wishlistProductIds.includes(pid)) {
                window.hulkappsWishlist.wishlistProductIds.push(pid);
              }
              
              // Store full item data with variant ID
              const itemExists = window.hulkappsWishlist.wishlistItems.some(
                item => item.productId === pid && item.variantId === variantId
              );
              if (!itemExists) {
                window.hulkappsWishlist.wishlistItems.push({
                  productId: pid,
                  variantId: variantId
                });
              }
            });

            // Notify Hulk app that guest wishlist data is loaded
            try {
              window.HulkappWishlist?._updateWishlistButton?.();

              // Set additional Hulk app variables for guest wishlist
              if (window.HulkappWishlist) {
                window.HulkappWishlist.guestId =
                  window.hulkappsWishlist.guestId;
                window.HulkappWishlist.wishlistProductIds =
                  window.hulkappsWishlist.wishlistProductIds;
              }
            } catch (e) {
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch wishlist data:", err);
      } finally {
        window.hulkappsWishlist._wishlistLoaded = true;

        // Update header count on every page load after wishlist data is fetched
        updateHeaderWishlistCount();
      }
    }

    window.WishlistCore = {
      DEFAULT_WISHLIST_NAME,
      createHeaders,
      createUrlWithParams,
      makeRequest,
      getDefaultWishlistId,
      addProductToDefaultWishlist,
      removeProductFromWishlist,
      findWishlistContainingProduct,
      markButtonAsAdded,
      markButtonAsRemoved,
      fetchWishlistData,
      fetchWishlistProducts,
      ready: null,
    };

    window.WishlistCore.ready = fetchWishlistData();
  }

  const Core = window.WishlistCore;

  const GUEST_UUID_KEY = "hulk_wishlist_guest_uuid";
  function getSavedGuestUuid() {
    try {
      return localStorage.getItem(GUEST_UUID_KEY) || null;
    } catch {
      return null;
    }
  }

  function setCookie(name, value, opts = {}) {
    const parts = [`${name}=${encodeURIComponent(value || "")}`];
    parts.push(`path=${opts.path || "/"}`);
    if (opts.maxAge !== undefined) parts.push(`max-age=${opts.maxAge}`);
    if (opts.sameSite) parts.push(`SameSite=${opts.sameSite}`);
    if (location.protocol === "https:") parts.push("Secure");
    document.cookie = parts.join(";");
  }

  function deleteCookie(name, path = "/") {
    setCookie(name, "", { path, maxAge: 0, sameSite: "Lax" });
  }

  function saveGuestUuid(uuid) {
    if (!uuid) return;
    window.hulkappsWishlist = window.hulkappsWishlist || {};
    window.hulkappsWishlist.guestId = uuid;
    try {
      localStorage.setItem(GUEST_UUID_KEY, uuid);
    } catch {}
    // let Hulk set its own cookie if available
    try {
      window.HulkappWishlist?._manageGuestCookie?.(uuid);
    } catch {}
    // belt & suspenders
    setCookie("hulk_wishlist_UserUUID", uuid, {
      path: "/",
      maxAge: 31536000,
      sameSite: "Lax",
    });
  }

  function clearGuestState() {
    try {
      localStorage.removeItem(GUEST_UUID_KEY);
    } catch {}
    deleteCookie("hulk_wishlist_UserUUID", "/");
    // also clear any proxy-path cookie just in case
    const gs = window.hulkappsWishlist?.generalSettings || {};
    const proxyPath = `/${gs.app_proxy_prefix || "apps"}/${
      gs.app_proxy_path || "advanced-wishlist"
    }`;
    deleteCookie("hulk_wishlist_UserUUID", proxyPath);

    if (window.hulkappsWishlist) window.hulkappsWishlist.guestId = "";
  }

  function whenWishlistLoaded(cb) {
    // calls cb once after data has been fetched at least once
    if (window.hulkappsWishlist?._wishlistLoaded) return cb();
    const iv = setInterval(() => {
      if (window.hulkappsWishlist?._wishlistLoaded) {
        clearInterval(iv);
        cb();
      }
    }, 150);
    setTimeout(() => clearInterval(iv), 7000); // safety cap
  }

  function watchGuestIdAndLoadOnce() {
    if (window.hulkappsWishlist?._watchedGuestId) return;
    window.hulkappsWishlist._watchedGuestId = true;

    const iv = setInterval(async () => {
      const gid = window.hulkappsWishlist?.guestId;
      if (gid) {
        clearInterval(iv);
        try {
          await Core.fetchWishlistData(); // populate ids
        } catch (e) {}
        // mark loaded happens inside fetchWishlistData
        hydrateCartWishlistHearts(); // run once data is in
        hydratePLPWishlistHearts(); // also hydrate PLP buttons for guests
      }
    }, 200);
    setTimeout(() => clearInterval(iv), 8000); // don't poll forever
  }

  /* ---------------- PLP (custom buttons, no Hulk modal) ---------------- */

  // Helper function to find the variant ID for a product in the wishlist
  function findWishlistVariantId(productId) {
    const items = window.hulkappsWishlist?.wishlistItems || [];
    const item = items.find(item => item.productId === Number(productId));
    return item ? item.variantId : null;
  }

  function createPLPWishlistButtons(container) {
    if (!container) return;
    const cards = container.querySelectorAll(
      ".card--product[data-wishlist-icon]"
    );
    cards.forEach((card) => {
      const productId = card.getAttribute("data-product-id");
      if (!productId) return;
      if (card.querySelector(".plp-wishlist-btn")) return;

      const added = (
        window.hulkappsWishlist?.wishlistProductIds || []
      ).includes(parseInt(productId, 10));

      // Try to get variant ID from wishlist data first, then fall back to card attributes
      let variantId = findWishlistVariantId(productId);
      if (!variantId) {
        variantId =
          card.getAttribute("data-variant-id") ||
          card.querySelector("quick-buy")?.getAttribute("data-product") ||
          null;
      }

      const a = document.createElement("a");
      a.className = "plp-wishlist-btn heart-wishlist-animation"; // not Hulk's classes
      a.setAttribute("data-wishlist-trigger", "plp");
      a.setAttribute("data-proid", productId);
      if (variantId) a.setAttribute("data-varid", variantId);
      a.setAttribute("data-added", added ? "true" : "false");
      a.href = "#"; // Always use # since we handle clicks with preventDefault

      const iconDiv = document.createElement("div");
      iconDiv.style.display = "initial";
      iconDiv.className = "wishlist_btn_icon";
      const outerIcon = document.createElement("i");
      outerIcon.className = "heart-hulk-animation";
      const innerIcon = document.createElement("i");
      innerIcon.className = "icon-wishlist-heart-empty";
      outerIcon.appendChild(innerIcon);
      iconDiv.appendChild(outerIcon);
      a.appendChild(iconDiv);

      card.insertBefore(a, card.firstChild);
    });
  }

  function bindPLPWishlistClicks() {
    document.addEventListener(
      "click",
      async (e) => {
        const btn = e.target.closest('[data-wishlist-trigger="plp"]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const isAdded = btn.getAttribute("data-added") === "true";
        const productId = btn.getAttribute("data-proid");
        const variantId = btn.getAttribute("data-varid");

        try {
          if (isAdded) {
            // Remove from wishlist
            await Core.removeProductFromWishlist({ productId, variantId });
            Core.markButtonAsRemoved(btn);
          } else {
            // Add to wishlist - open modal for variant selection
            const card = btn.closest(".card--product");

            // find the matching <quick-buy> for this product
            let qb = card?.querySelector("quick-buy");
            if (!qb) {
              qb = document.querySelector(
                `quick-buy[data-product="${productId}"]`
              );
            }
            if (!qb) {
              return;
            }

            // mark intent on the instance
            qb._wishlistSourceButton = btn;
            qb.isWishlistMode = true;

            // If modal already exists, toggle UI now and open it, bypassing theme opener
            const existing = document.getElementById(qb.targetModalId);
            if (existing) {
              qb.modal = existing;
              if (typeof qb.__applyWishlistUIMode === "function") {
                qb.__applyWishlistUIMode();
              }
              window.Woolman?.ModalsAndDrawers?.showModalOrDrawer?.(
                qb.targetModalId
              );
              return;
            }

            // No modal yet: call the normal opener (our override will carry wishlist mode through)
            const opener = qb.onModalRequestOpen || qb.onOpen || function () {};
            opener.call(qb, new Event("open"));
            return;
          }

          // Update header count after wishlist changes
          updateHeaderWishlistCount();
        } catch (err) {
          console.error(
            isAdded
              ? "Remove from wishlist failed:"
              : "Add to wishlist failed:",
            err
          );
        }
      },
      true
    );
  }

  /* ---------------- Cart: move to default wishlist ---------------- */

  function bindCartSaveForLater() {
    document.addEventListener("click", async (e) => {
      const btn =
        e.target.closest("[data-hulkSaveForLater]") ||
        e.target.closest('[data-wishlist-trigger="cart"]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const productId =
        btn.getAttribute("data-product-id") || btn.getAttribute("data-proid");
      const variantId =
        btn.getAttribute("data-variant-id") || btn.getAttribute("data-varid");
      const lineRaw = btn.getAttribute("data-line");
      const line = lineRaw ? Number(lineRaw) : null;
      const isAdded = btn.getAttribute("data-added") === "true";

      try {
        if (isAdded) {
          // Remove from wishlist
          await Core.removeProductFromWishlist({ productId, variantId });
          Core.markButtonAsRemoved(btn);
        } else {
          // Add to wishlist
          const result = await Core.addProductToDefaultWishlist({ productId, variantId });
          // Get the actual variant ID from the response if available
          const actualVariantId = result?.data?.variant_id || variantId;
          Core.markButtonAsAdded(btn, actualVariantId);

          // If this was from cart line item, remove from cart
          if (line) {
            await fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ line, quantity: 0 }),
            });
            location.reload();
            return; // Don't update UI since page will reload
          }
        }

        // Update wishlist button states
        try {
        } catch (e) {
          hydrateCartWishlistHearts();
          hydratePLPWishlistHearts();
        }

        // Update header count after wishlist changes
        updateHeaderWishlistCount();
      } catch (err) {
        console.error(
          isAdded ? "Remove from wishlist failed:" : "Save for later failed:",
          err
        );
      }
    });
  }

  // Color cart hearts that are already in wishlist (on load / re-render)
  function hydrateCartWishlistHearts() {
    const ids = window.hulkappsWishlist?.wishlistProductIds || [];
    if (!ids.length) return;

    document
      .querySelectorAll('[data-wishlist-trigger="cart"]')
      .forEach((btn) => {
        const pid = parseInt(
          btn.getAttribute("data-product-id") ||
            btn.getAttribute("data-proid") ||
            "",
          10
        );
        if (!Number.isNaN(pid) && ids.includes(pid)) {
          Core.markButtonAsAdded(btn);
        }
      });
  }

  // Color PLP hearts that are already in wishlist (on load / re-render)
  function hydratePLPWishlistHearts() {
    const ids = window.hulkappsWishlist?.wishlistProductIds || [];
    if (!ids.length) return;

    document
      .querySelectorAll('[data-wishlist-trigger="plp"]')
      .forEach((btn) => {
        const pid = parseInt(btn.getAttribute("data-proid") || "", 10);
        if (!Number.isNaN(pid) && ids.includes(pid)) {
          // Set the correct variant ID from wishlist data
          const variantId = findWishlistVariantId(pid);
          if (variantId) {
            btn.setAttribute("data-varid", variantId);
          }
          Core.markButtonAsAdded(btn);
        }
      });
  }

  // Watch for drawer/cart DOM updates and re-hydrate hearts

  //KÄYTÄ TAIGAN OMAA !!!!!!!!!!!
  function observeCartForUpdates() {
    let timer = null;
    const needsHydrate = (node) => {
      if (node.nodeType !== 1) return false;
      if (node.matches?.(".cart, .cart-drawer")) return true;
      return !!node.querySelector?.('[data-wishlist-trigger="cart"]');
    };

    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (needsHydrate(n)) {
            clearTimeout(timer);
            timer = setTimeout(hydrateCartWishlistHearts, 120);
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
  /* ---------------- QuickBuy augmentation (no duplicate helpers) ---------------- */

  function augmentQuickBuyPrototype() {
    const Ctor = customElements.get("quick-buy");
    if (!Ctor) return;

    const proto = Ctor.prototype;
    if (proto.__wlPatched) return;
    proto.__wlPatched = true;

    const origOnModalRequestOpen = proto.onModalRequestOpen;
    const origHookEvents = proto.hookEvents;
    const origOnVariantChange = proto.onVariantChange;

    // Helper: apply wishlist vs normal UI to an already-built modal
    proto.__applyWishlistUIMode = function () {
      // Always work inside the currently-open modal DOM
      const modalRoot =
        this.modal || document.getElementById(this.targetModalId);
      if (!modalRoot) return;

      // Find the product form element inside the modal, regardless of exact id/tag
      const productFormComponent =
        modalRoot.querySelector("product-form") ||
        modalRoot.querySelector('[id^="product-form-"]') ||
        null;

      // Remove any previously injected wishlist wrapper/button
      this.wishlistButton?.parentNode?.remove();
      this.wishlistButton = null;

      if (this.isWishlistMode) {
        if (productFormComponent) productFormComponent.style.display = "none";

        const wrap = document.createElement("div");
        wrap.className = "product-form__buttons";
        wrap.style.marginTop = "1rem";

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn btn--primary btn--full";
        btn.id = "wishlist-save-btn";
        btn.textContent = "Save to wishlist";
        btn.dataset.proID = this.dataset.product;

        // Try currentVariant, fall back to the hidden input inside this modal
        const variantInput =
          modalRoot.querySelector('input[name="id"]') ||
          productFormComponent?.querySelector?.('input[name="id"]');

        const currentVariantId = this.currentVariant?.id || variantInput?.value;
        if (currentVariantId) btn.dataset.varID = currentVariantId;

        btn.addEventListener("click", async () => {
          try {
            btn.disabled = true;
            const result = await Core.addProductToDefaultWishlist({
              productId: btn.dataset.proID,
              variantId: btn.dataset.varID,
            });
            // Get the actual variant ID from the response if available
            const actualVariantId = result?.data?.variant_id || btn.dataset.varID;
            if (this.wishListSourceButton) {
              Core.markButtonAsAdded(this.wishListSourceButton, actualVariantId);
            } else {
              // Fallback: find the PLP wishlist button by product ID
              const productId = btn.dataset.proID;
              const plpButton = document.querySelector(
                `[data-wishlist-trigger="plp"][data-proid="${productId}"]`
              );
              if (plpButton) {
                Core.markButtonAsAdded(plpButton, actualVariantId);
              } else {
              }
            }

            // Update cart hearts immediately after adding to wishlist
            hydrateCartWishlistHearts();
            hydratePLPWishlistHearts();

            // Update header count after adding to wishlist
            updateHeaderWishlistCount();

            const overlay = modalRoot.querySelector("[data-overlay]");
            overlay?.click();
            window.Woolman?.ModalsAndDrawers?.closeModalOrDrawer?.(
              this.targetModalId
            );
          } catch (err) {
            console.error("Saving to wishlist failed:", err);
            btn.disabled = false;
          } finally {
            this.isWishlistMode = false;
            this.wishListSourceButton = null;
          }
        });

        wrap.appendChild(btn);
        if (productFormComponent && productFormComponent.parentNode) {
          productFormComponent.parentNode.insertBefore(
            wrap,
            productFormComponent.nextSibling
          );
        }
        this.wishlistButton = btn;
      } else {
        // Normal QuickBuy: ensure form is visible
        if (productFormComponent) productFormComponent.style.display = "";
      }
    };

    // Stable entry for PLP to open in wishlist mode
    proto.openForWishlist = function (sourceBtn) {
      this._wishlistSourceButton = sourceBtn || null;
      this.isWishlistMode = true;
      const opener = this.onModalRequestOpen || this.onOpen || function () {};
      return opener.call(this, new Event("open"));
    };

    // Prefer current click target; only fall back to cached source for openForWishlist()
    if (typeof origOnModalRequestOpen === "function") {
      proto.onModalRequestOpen = function (event) {
        event?.preventDefault?.();
        const currentSrc = event?.target?.closest?.(
          ".plp-wishlist-btn, .grid-wishlist-btn, [data-wishlist]"
        );

        // Use cached source only if:
        // 1. Current click is from a wishlist button, OR
        // 2. We have a cached source AND the current event is synthetic (from wishlist flow)
        const isSyntheticEvent = event.type === "open" && !event.target;
        const srcBtn =
          currentSrc || (isSyntheticEvent ? this._wishlistSourceButton : null);

        if (srcBtn) {
          this.isWishlistMode = true;
          this.wishListSourceButton = srcBtn;
        } else {
          this.isWishlistMode = false;
          this.wishListSourceButton = null;
          // Clear any cached wishlist source when opening in normal mode
          this._wishlistSourceButton = null;
        }
        // one-shot the cache so previous opens can't leak
        this._wishlistSourceButton = null;

        // If the modal already exists, toggle UI now and show it
        const existing = document.getElementById(this.targetModalId);
        if (existing) {
          this.modal = existing;
          this.__applyWishlistUIMode();
          window.Woolman?.ModalsAndDrawers?.showModalOrDrawer?.(
            this.targetModalId
          );
          return;
        }

        // Otherwise build via original (hookEvents will apply wishlist UI)
        return origOnModalRequestOpen.call(this, event || new Event("open"));
      };
    }

    // After modal build, inject/remove wishlist UI based on current mode
    if (typeof origHookEvents === "function") {
      proto.hookEvents = function (...args) {
        origHookEvents.apply(this, args);

        // Always (re)apply UI on first build
        this.__applyWishlistUIMode();

        // Reset wishlist mode on close (bind once per element)
        if (!this.__wlCloseBound) {
          const resetWishlistMode = () => {
            this.isWishlistMode = false;
            this._wishlistSourceButton = null;
            this.wishListSourceButton = null;
            // Also reset any existing modal UI to normal mode
            if (this.modal) {
              this.__applyWishlistUIMode();
            }
          };

          this.modal
            ?.querySelector?.("[data-overlay]")
            ?.addEventListener("click", resetWishlistMode);
          this.modal
            ?.querySelector?.(
              "[data-close], .modal__close, button[aria-label='Close']"
            )
            ?.addEventListener("click", resetWishlistMode);
          this.addEventListener?.("close", resetWishlistMode);

          this.__wlCloseBound = true;
        }
      };
    }

    // Keep variant id in sync with the injected button
    if (typeof origOnVariantChange === "function") {
      proto.onVariantChange = function (...args) {
        origOnVariantChange.apply(this, args);
        if (this.isWishlistMode && this.wishlistButton && this.currentVariant) {
          this.wishlistButton.dataset.varID = this.currentVariant.id;
        }
      };
    }
  }

  // Patch QuickBuy no matter the load order
  if (customElements.get("quick-buy")) {
    augmentQuickBuyPrototype();
  } else {
    customElements.whenDefined("quick-buy").then(augmentQuickBuyPrototype);
  }

  /* ---------------- Boot ---------------- */

  let _guestMigratedThisSession = false;

  async function migrateGuestToCustomerIfNeeded() {
    if (_guestMigratedThisSession) return;

    const cid = window.hulkappsWishlist?.customerId;
    const gid = getSavedGuestUuid() || window.hulkappsWishlist?.guestId || null;
    if (!cid || !gid) return;

    try {
      // 0) Try Hulk’s helper, but do NOT return; we’ll still do manual migration to be sure
      try {
        await window.HulkappWishlist?._addFromGuestToData?.();
      } catch (e) {
        // ignore; we’ll handle manually
      }

      // 1) Read guest items
      const guestUrl = Core.createUrlWithParams(
        "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/products/guest",
        { uuid: gid }
      );
      const guestResp = await Core.makeRequest(
        guestUrl,
        Core.createHeaders(false)
      );
      const items = Array.isArray(guestResp?.data) ? guestResp.data : [];

      if (!items.length) {
        // Even if none, clear the stale UUID so future logouts don’t 404
        clearGuestState();
        await Core.fetchWishlistData();
        _guestMigratedThisSession = true;
        return;
      }

      // 2) Ensure default list and add each item
      const listId = await Core.getDefaultWishlistId();

      for (const it of items) {
        const payload = {
          product_id: String(it.product_id),
          customer_id: String(cid),
          wishlist_ids: [Number(listId)],
          ...(it.variant_id ? { variant_id: String(it.variant_id) } : {}),
        };
        const res = await fetch(
          "https://advanced-wishlist.hulkapps.com/api/public/v1/wishlist/add_product",
          {
            method: "POST",
            headers: Core.createHeaders(),
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok && res.status !== 422) {
          const t = await res.text().catch(() => "");
          console.warn("migrate add failed", res.status, t, payload);
        }
      }

      // 3) Now it’s safe to clear guest state and refetch lists
      clearGuestState();

      // Fetch twice with a tiny delay to avoid timing issues on Hulk’s side
      await Core.fetchWishlistData();
      setTimeout(() => Core.fetchWishlistData().catch(() => {}), 250);

      _guestMigratedThisSession = true;
    } catch (err) {
    }
  }

  // Track if buttons have been created to avoid race conditions
  let buttonsCreated = false;
  let eventListenersBound = false;

  // Function to ensure proper sequencing
  async function ensureProperInitialization() {
    if (!eventListenersBound) {
      bindPLPWishlistClicks();
      bindCartSaveForLater();
      eventListenersBound = true;
    }

    if (!buttonsCreated) {
      await Core.ready;
      createPLPWishlistButtons(document);
      buttonsCreated = true;
    }

    // Hydrate hearts after both buttons are created and listeners are bound
    whenWishlistLoaded(() => {
      hydrateCartWishlistHearts();
      hydratePLPWishlistHearts();
    });
  }

  document.addEventListener("nosto:rendered", async (e) => {
    await Core.ready;
    if (e.detail?.placement) {
      createPLPWishlistButtons(e.detail.placement);
    } else if (e.detail?.placements) {
      for (const id of e.detail?.placements) {
        const container = document.querySelector(`[data-placement-id="${id}"]`);
        if (container) createPLPWishlistButtons(container);
      }
    }
    
    // Ensure proper initialization after Nosto renders
    await ensureProperInitialization();
  });

  document.addEventListener("DOMContentLoaded", async () => {
    
    // Initialize wishlist state
    if (!window.hulkappsWishlist.wishlistProductIds) {
      window.hulkappsWishlist.wishlistProductIds = [];
    }
    if (!window.hulkappsWishlist.wishlistItems) {
      window.hulkappsWishlist.wishlistItems = [];
    }

    // If we are logged in, try to migrate any leftover guest list once
    if (window.hulkappsWishlist?.customerId) {
      await migrateGuestToCustomerIfNeeded();
    } else {
      // guest: restore cached uuid if any
      if (!window.hulkappsWishlist?.guestId) {
        const cached = getSavedGuestUuid();
        if (cached) saveGuestUuid(cached);
      }
    }

    // Bind event listeners immediately (these work on existing elements)
    bindPLPWishlistClicks();
    bindCartSaveForLater();
    eventListenersBound = true;

    // Set up other functionality
    watchGuestIdAndLoadOnce();
    observeCartForUpdates();

    // For non-Nosto content, create buttons immediately
    // For Nosto content, wait for nosto:rendered event
    setTimeout(async () => {
      if (!buttonsCreated) {
        await Core.ready;
        createPLPWishlistButtons(document);
        buttonsCreated = true;
        
        // Hydrate hearts after buttons are created
        whenWishlistLoaded(() => {
          hydrateCartWishlistHearts();
          hydratePLPWishlistHearts();
        });
      }
    }, 100); // Small delay to allow Nosto to render first
  });
})();
